import classNames from 'classnames';
import jsmediatags from 'jsmediatags';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import useNavigationShortcuts from './components/common/hooks/useNavigationShortcuts';
import ShortcutsOverlay from './components/common/shortcutsOverlay/shortcutsOverlay';
import Footer from './components/footer/footer';
import Header from './components/header/header';
import LoadingComponent from './components/settingsAndStartup/loadingComponent';
import SetupComponent from './components/settingsAndStartup/setupComponent';
import noop from './utilities/noop';
import ContentPageHeader from './pages/contentPageHeader/header';
import Frontpage from './pages/frontpage/frontpage';
import Help from './pages/troubleshooting/troubleshooting';
import routes from './routes';
import ActivePlaylist from './services/activePlaylist/activePlaylist';
import ColumnSettingsService from './services/columnSettings/columnSettings';
import DropboxRedirect from './services/dropbox/dropboxRedirect';
import DropboxService from './services/dropbox/dropboxService';
import GoogleApiService, { GoogleApiContext } from './services/gapi/gapiService';
import GoogleRedirect from './services/gapi/googleRedirect';
import LibraryService, { LibraryContext } from './services/library/libraryService';
import OneDriveRedirect from './services/oneDrive/oneDriveRedirect';
import OneDriveService from './services/oneDrive/oneDriveService';
import PodcastService from './services/podcasts/podcastService';
import ScreenDetection from './services/screenDetection/screenDetection';
import SmallDeviceService from './services/smallDevices/smallDeviceService';
import SpotifyRedirect from './services/spotify/spotifyRedirect';
import SpotifyService from './services/spotify/spotifyService';
import SoundCloudRedirect from './services/soundCloud/soundCloudRedirect';
import SoundCloudService from './services/soundCloud/soundCloudService';
import SyncService from './services/synchronization/syncService';
import { Context } from './services/_globalContext/context';
import ContextService from './services/_globalContext/contextService';
import { isTouchDevice } from './settings';
import './styles/App.scss';

export interface IRoute {
  component: React.ComponentType;
  /** Where the menu entry and the `g` chord go. Required when `path` has a `/*` tail. */
  link?: string;
  /** The route pattern: exact, or ending in `/*` to take everything below it. */
  path: string;
  name: string;
  icon?: string;
  /** Overrides the 1em default; the SoundCloud wordmark is far wider than the others at the same height. */
  iconHeight?: string;
}

export default function App(props: any) {
  useEffect(function configureJsMediaTags() {
    jsmediatags.Config.setDisallowedXhrHeaders(['if-modified-since']);
    jsmediatags.Config.setXhrTimeoutInSec(900);
  }, []);

  useEffect(function disableLoggingInProduction() {
    if (process.env.NODE_ENV === 'production') {
      console.debug = noop;
      console.info = noop;
      console.log = noop;
    }
  }, []);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <article className="Frontpage">
            <ContentPageHeader />
            <Frontpage />
          </article>
        }
      />
      {/*
        No /privacy route. The policy is public/privacy.html - a real file, so
        that a fetch of the url returns the text - and the host serves it ahead
        of the SPA fallback: an explicit /privacy rewrite in
        public/staticwebapp.config.json. Links to it are <a href>, never <Link>.
      */}
      <Route
        path="/help"
        element={
          <article className="Frontpage">
            <ContentPageHeader />
            <Help />
          </article>
        }
      />
      <Route path="/dropbox-redirect" element={<DropboxRedirect />} />
      <Route path="/spotify-redirect" element={<SpotifyRedirect />} />
      <Route path="/soundcloud-redirect" element={<SoundCloudRedirect />} />
      {/*
        Outside /app on purpose. These run before the service tree mounts, so
        the token is in hand by the time anything asks whether we are signed in.
      */}
      <Route path="/google-redirect" element={<GoogleRedirect />} />
      <Route path="/onedrive-redirect" element={<OneDriveRedirect />} />
      {/* The pages under /app are matched by AppInner, once the services are up. */}
      <Route path="/app/*" element={<AppServices />} />
      <Route path="*" element={<p>Page not found</p>} />
    </Routes>
  );
}

const AppServices = () => (
  <ScreenDetection>
    <SmallDeviceService>
      {/** Keep early to ensure errors can be displayed with snackbar */}
      <ContextService>
        <ColumnSettingsService>
          <ActivePlaylist>
            <DropboxService>
              <OneDriveService>
                <GoogleApiService>
                  <SpotifyService>
                    <SoundCloudService>
                      <LibraryService>
                        <SyncService>
                          <PodcastService>
                            <AppInner />
                          </PodcastService>
                        </SyncService>
                      </LibraryService>
                    </SoundCloudService>
                  </SpotifyService>
                </GoogleApiService>
              </OneDriveService>
            </DropboxService>
          </ActivePlaylist>
        </ColumnSettingsService>
      </ContextService>
    </SmallDeviceService>
  </ScreenDetection>
);

/**
 * The page routes are descendants of the `/app/*` route above, and a
 * descendant route's path is relative to its parent: `/app/library/*` would
 * never match there, `library/*` does. routes.ts keeps the absolute form
 * because the header links and the `g` chords need it.
 */
function underApp(path: string) {
  if (!path.startsWith('/app/')) {
    throw new Error(`Route ${path} is not under /app`);
  }

  return path.slice('/app/'.length);
}

const AppInner = () => {
  const ctx = useContext(Context);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const gapiCtx = useContext(GoogleApiContext);
  const libCtx = useContext(LibraryContext);

  const [didAutoConfigure, setDidAutoConfigure] = useState(false);

  useNavigationShortcuts(routes);

  const externalLoaded =
    gapiCtx.dataApiLoaded && gapiCtx.iframeApiLoaded && gapiCtx.gapiClientLoaded;
  const appLoaded = ctx.settingsLoaded && libCtx.libraryLoaded && libCtx.playlistsLoaded;

  /**
   * Attempt to autoconfigure storage provider using localStorage setting.
   */
  useEffect(
    function autoConfigure() {
      if (externalLoaded && !didAutoConfigure) {
        ctxRef.current.autoConfigureStorageProvider();
        setDidAutoConfigure(true);
      }
    },
    [externalLoaded, didAutoConfigure],
  );

  return (
    <article
      className={classNames('App', 'ViewportHeight', {
        'touch-device': isTouchDevice,
      })}
    >
      <Header routes={routes} setup={!ctx.storageProvider.current} />
      <main className={ctx.playerSize}>
        {(() => {
          // We do not return an inline function component
          // We execute an inline function, returning stable component types
          if (externalLoaded) {
            if (appLoaded && ctx.storageProvider.current) {
              return (
                <Routes>
                  {routes.map((route) => (
                    <Route
                      key={route.path}
                      path={underApp(route.path)}
                      element={<route.component />}
                    />
                  ))}
                  <Route index element={<Navigate to="/app/library" replace />} />
                  <Route path="*" element={<p>Page not found</p>} />
                </Routes>
              );
            } else if (didAutoConfigure && !ctx.storageProvider.current) {
              return <SetupComponent />;
            } else {
              return <LoadingComponent />;
            }
          } else {
            return <LoadingComponent />;
          }
        })()}
      </main>
      <Footer />
      <ShortcutsOverlay />
    </article>
  );
};
